import { useState, type Ref } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import DownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import { useToast } from './ToastProvider';

/** Query-parameter values an export endpoint accepts; `undefined` entries are dropped by axios. */
export type ExportParams = Record<string, string | number | boolean | undefined>;

export interface ExportButtonProps {
  /** Export endpoint path, relative to the api client's base URL (e.g. `/lines/export`). */
  path: string;
  /** Query parameters — the caller's current filter set. */
  params?: ExportParams;
  /** Filename used when the response carries no readable `Content-Disposition`. */
  fallbackFilename: string;
  /** Disable for a caller-owned reason (nothing to export, no scope chosen yet). */
  disabled?: boolean;
  /** Render disabled with an "unavailable" tooltip instead of wiring the export. */
  unavailable?: boolean;
  /** Tooltip shown when {@link unavailable}; defaults to the generic copy. */
  unavailableTooltip?: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * UTF-8 byte-order mark, prefixed to every exported CSV.
 *
 * Excel on Windows decodes a BOM-less `.csv` with the system ANSI code page rather than UTF-8,
 * so without it "Renée Côté" opens as "RenÃ©e CÃ´té" — and these exports are the
 * name-carrying ones (User, Manager, Position, Plan, Device), for customers whose locales are
 * es-MX and fr-CA as much as en-US.
 *
 * Built with `fromCharCode` rather than written as a source literal: the character itself is
 * invisible in an editor and can be silently stripped by tooling that normalizes source
 * encoding. This form is unambiguous to a reader, a diff and a linter alike.
 */
const UTF8_BOM = String.fromCharCode(0xfeff);

/** Pulls the server-provided filename out of a Content-Disposition header. */
function filenameFrom(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * CSV-only export trigger. Fetches `path` (with `params`) as text through the
 * shared `apiClient` — so the MSAL bearer and `Accept-Language` are carried,
 * which a bare anchor `href` would send neither of — then streams the response to
 * a client download, naming the file from `Content-Disposition` where the browser
 * can read it and from `fallbackFilename` where it cannot (a cross-origin
 * response without the header exposed). Shows a pending state while the request
 * is in flight and toasts on failure. PDF is deferred (TD-181), so there is no
 * format menu.
 *
 * <p>Deliberately generic in what it exports (AU-61). It backs the Reports
 * exports through `ReportExportButton`, which owns the report-shaped props and
 * the scope rules, and the Lines / Users list exports directly. The knowledge
 * this component holds is "how a CSV download works in this app" — not which
 * endpoints exist.</p>
 */
export function ExportButton({
  path,
  params,
  fallbackFilename,
  disabled,
  unavailable,
  unavailableTooltip,
  ref,
}: ExportButtonProps) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const [pending, setPending] = useState(false);

  const run = async () => {
    setPending(true);
    try {
      // Fetch as text and blob it client-side: CSV is text, so this carries the
      // MSAL bearer + Accept-Language through apiClient without a binary
      // response type, and downloads identically to a native blob.
      const response = await apiClient.get<string>(path, {
        params,
        responseType: 'text',
        headers: { Accept: 'text/csv' },
      });
      const filename = filenameFrom(
        response.headers['content-disposition'] as string | undefined,
        fallbackFilename,
      );
      // The BOM goes on here rather than server-side, deliberately. The file the
      // user saves is built from `response.data` — a string axios has already
      // decoded — so a BOM in the response body would either be re-encoded away
      // or, where the decoder does not strip it, survive into a doubled BOM that
      // renders as `ï»¿` in the first cell. Prefixing the blob is the one place
      // that is deterministic. It also leaves the endpoints emitting clean
      // BOM-less UTF-8 for script and Bruno consumers, matching the convention
      // `DelimitedFileRenderer` sets for the partner feeds.
      const blob = new Blob([UTF8_BOM, response.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('export.failedTitle'), t('export.failedBody'));
    } finally {
      setPending(false);
    }
  };

  const button = (
    <span>
      <Button
        ref={ref}
        variant="contained"
        size="small"
        disabled={unavailable || disabled || pending}
        startIcon={pending ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlined />}
        onClick={() => void run()}
      >
        {pending ? t('export.pending') : t('export.label')}
      </Button>
    </span>
  );

  // The tooltip wraps a span so it still shows while the button is disabled.
  return unavailable ? (
    <Tooltip title={unavailableTooltip ?? t('export.unavailable')}>{button}</Tooltip>
  ) : (
    button
  );
}
